import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  fetchPitchFeedJson,
  PUBLIC_SITE_FEED_LANG,
} from "../api/pitchFeed";
import { htmlToPlainText } from "../utils/html";
import { resolveArticleRouteId } from "../utils/articleId";
import { initialNews } from "../data/mockData";

const NewsContext = createContext();

export const useNews = () => useContext(NewsContext);

/** Map API item using top-level fields (English wire copy when `?lang=EN`). */
export function mapApiItemToArticle(item) {
  const headline =
    (item.headline ?? item.title ?? "").trim() || "(No title)";
  const bodyHtml = item.body_html ?? "";
  const bodyPlain = htmlToPlainText(bodyHtml);
  const summarySlot = (item.summary ?? "").trim();
  const excerpt = summarySlot || headline;
  const content = bodyPlain || excerpt || headline;
  const ext = item.external_id != null ? String(item.external_id) : "";
  const primaryId = item.id != null ? String(item.id) : ext;
  return {
    id: primaryId,
    externalId: ext || primaryId,
    title: headline,
    excerpt,
    bodyHtml,
    category: (item.category && String(item.category).trim()) || "NEWS",
    date: item.published_at,
    imageUrl: item.imageUrl ?? null,
    author: "",
    content,
  };
}

/** Demo rows: English copy on top-level fields. */
function mockRowToFeedItem(m) {
  const pub =
    typeof m.date === "string" ? m.date : m.date?.toISOString?.() ?? "";
  const en = m.locales?.EN ?? m.locales?.en;
  const pick = en ?? m.locales?.DA;
  const safeHtml = (plain) =>
    `<p>${String(plain ?? "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`;
  const title = pick?.title ?? "";
  const excerpt = pick?.excerpt ?? "";
  const content = pick?.content ?? excerpt;
  return {
    id: m.id,
    external_id: m.id,
    headline: title,
    body_html: safeHtml(content || excerpt),
    summary: excerpt,
    category: m.category,
    published_at: pub,
    ingested_at: pub,
    imageUrl: m.imageUrl,
  };
}

export const NewsProvider = ({ children }) => {
  const location = useLocation();
  const [rawFeedItems, setRawFeedItems] = useState([]);
  const [feedStatus, setFeedStatus] = useState({
    loading: true,
    error: null,
    source: "mock",
  });

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) {
      setFeedStatus((s) => ({ ...s, loading: false }));
      return undefined;
    }
    const ac = new AbortController();
    let cancelled = false;
    setFeedStatus((s) => ({
      ...s,
      loading: true,
      error: null,
    }));
    (async () => {
      try {
        const data = await fetchPitchFeedJson({
          signal: ac.signal,
          locale: PUBLIC_SITE_FEED_LANG,
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        if (cancelled) return;
        if (items.length === 0) {
          setRawFeedItems([]);
          setFeedStatus({
            loading: false,
            error:
              "Live feed is empty — run POST /internal/sync/goalise on the API.",
            source: "mock",
          });
          return;
        }
        setRawFeedItems(items);
        setFeedStatus({
          loading: false,
          error: null,
          source: "api",
          customer: data.customer,
        });
      } catch (e) {
        if (cancelled || e?.name === "AbortError") return;
        console.error("Pitch feed API:", e);
        setRawFeedItems([]);
        setFeedStatus({
          loading: false,
          error: e?.message || "Feed unavailable",
          source: "mock",
        });
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [location.pathname]);

  const articles = useMemo(() => {
    if (rawFeedItems.length > 0) {
      return rawFeedItems.map((item) => mapApiItemToArticle(item));
    }
    return initialNews.map((m) => mapApiItemToArticle(mockRowToFeedItem(m)));
  }, [rawFeedItems]);

  const addArticle = useCallback((newArticle) => {
    const article = {
      ...newArticle,
      id: `NEWS-${Date.now()}`,
      date: new Date().toISOString(),
      author: newArticle.author || "Bureau Desk",
    };
    const body_html = `<p>${String(article.content ?? "").replace(/</g, "")}</p>`;
    setRawFeedItems((prev) => [
      {
        id: article.id,
        external_id: article.id,
        headline: article.title,
        body_html,
        summary: article.excerpt,
        category: article.category,
        published_at: article.date,
        ingested_at: article.date,
      },
      ...prev,
    ]);
  }, []);

  const getArticleById = useCallback(
    (rawId) => {
      const base = decodeURIComponent(String(rawId ?? "")).trim();
      const resolved = resolveArticleRouteId(base);
      const needles = new Set([
        base.toLowerCase(),
        resolved.toLowerCase(),
      ]);
      if (/^\d+$/.test(base)) needles.add(`goalise-${base}`.toLowerCase());
      if (base.includes("_")) needles.add(base.replace(/_/g, "-").toLowerCase());
      if (base.includes("-")) needles.add(base.replace(/-/g, "_").toLowerCase());
      return articles.find((a) => {
        const id = String(a.id ?? "").toLowerCase();
        const ext = String(a.externalId ?? "").toLowerCase();
        for (const n of needles) {
          if (n && (id === n || ext === n)) return true;
        }
        return false;
      });
    },
    [articles],
  );

  return (
    <NewsContext.Provider
      value={{
        articles,
        addArticle,
        getArticleById,
        feedStatus,
      }}
    >
      {children}
    </NewsContext.Provider>
  );
};